import {useState, useEffect} from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from '../lib/toast.js';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

export default function AdobeSignCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('processing'); // processing, success, error
    const [errorMessage, setErrorMessage] = useState('');
    const isPopup = window.opener !== null;

    useEffect(() => {
        handleCallback();
    }, []);

    async function handleCallback() {
        const code = searchParams.get('code');
        const apiAccessPoint = searchParams.get('api_access_point');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for OAuth errors from Adobe
        if (error) {
            setStatus('error');
            const errMsg = errorDescription || 'Authorization was denied or cancelled.';
            setErrorMessage(errMsg);
            if (isPopup && window.opener) {
                window.opener.postMessage({
                    type: 'ADOBE_SIGN_AUTH_COMPLETE',
                    success: false,
                    error: errMsg
                }, '*');
            }
            return;
        }

        // Validate we have the code
        if (!code) {
            setStatus('error');
            const errMsg = 'Missing authorization code from Adobe Sign.';
            setErrorMessage(errMsg);
            if (isPopup && window.opener) {
                window.opener.postMessage({
                    type: 'ADOBE_SIGN_AUTH_COMPLETE',
                    success: false,
                    error: errMsg
                }, '*');
            }
            return;
        }

        // If in popup, send code to parent window and let parent handle the API call
        if (isPopup && window.opener) {
            setStatus('success');
            window.opener.postMessage({
                type: 'ADOBE_SIGN_AUTH_CODE',
                code: code,
                api_access_point: apiAccessPoint || null
            }, '*');
            setTimeout(() => window.close(), 1000);
        } else {
            // Not in popup - shouldn't normally happen, redirect to integration manager
            setStatus('error');
            setErrorMessage('Please use the Connect button from the Integration Manager.');
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
                    Adobe Sign Authorization
                </h2>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    {status === 'processing' && (
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-sm text-gray-600">Completing authorization...</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="text-center">
                            <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto" />
                            <p className="mt-4 text-lg font-medium text-gray-900">Successfully Connected!</p>
                            <p className="mt-2 text-sm text-gray-600">
                                {isPopup ? 'This window will close automatically...' : 'Redirecting to Integration Manager...'}
                            </p>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="text-center">
                            <XCircleIcon className="h-12 w-12 text-red-500 mx-auto" />
                            <p className="mt-4 text-lg font-medium text-gray-900">Authorization Failed</p>
                            <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                            <button
                                onClick={() => isPopup ? window.close() : navigate('/integration-manager')}
                                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                {isPopup ? 'Close Window' : 'Return to Integration Manager'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
}
